/* eslint-disable react/prop-types */
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  fetchUserNotifications,
  functionMarkAsRead,
} from "../../../services/NotificationsServices";
import socket from "../../../services/socket";
import "./notificationsPage.css";

const RESERVATION_TYPES = new Set(["event_reservation", "table_reservation"]);

const TABS = [
  { id: "all", labelKey: "notifFilterAll" },
  { id: "unread", labelKey: "notifFilterUnread" },
  { id: "event_reservation", labelKey: "notifFilterReservations" },
  { id: "order", labelKey: "notifFilterOrders" },
  { id: "support_reply", labelKey: "notifFilterSupport" },
];

const TYPE_LABEL_KEYS = {
  event_reservation: "notifFilterReservations",
  table_reservation: "notifFilterTableReservations",
  client_reclamation: "claims",
  order: "notifFilterOrders",
  support_reply: "notifFilterSupport",
};

const EventIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={20}
    height={20}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
    <line x1="16" y1="2" x2="16" y2="6" />
    <line x1="8" y1="2" x2="8" y2="6" />
    <line x1="3" y1="10" x2="21" y2="10" />
  </svg>
);

const OrderIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={20}
    height={20}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="9" cy="21" r="1" />
    <circle cx="20" cy="21" r="1" />
    <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
  </svg>
);

const TableIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={20}
    height={20}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M4 10h16" />
    <path d="M6 10V7a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v3" />
    <path d="M8 10v9" />
    <path d="M16 10v9" />
  </svg>
);

const BellIcon = ({ size = 20 }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
    <path d="M13.73 21a2 2 0 0 1-3.46 0" />
  </svg>
);

const SupportIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={20}
    height={20}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
  </svg>
);

const CheckIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={16}
    height={16}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2.4}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const Notifications = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("all");

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const data = await fetchUserNotifications();
        if (!cancelled) {
          setNotifications(
            Array.isArray(data?.notifications) ? data.notifications : []
          );
        }
      } catch (error) {
        console.error("Error loading notifications:", error);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const handleNew = (notif) => {
      if (!notif) return;
      setNotifications((prev) => [notif, ...prev]);
    };
    socket.on("new-event-reservation", handleNew);
    socket.on("new-table-reservation", handleNew);
    socket.on("new-support-reply", handleNew);
    socket.on("new-client-reclamation", handleNew);
    return () => {
      socket.off("new-event-reservation", handleNew);
      socket.off("new-table-reservation", handleNew);
      socket.off("new-support-reply", handleNew);
      socket.off("new-client-reclamation", handleNew);
    };
  }, []);

  const unreadCount = useMemo(
    () => notifications.filter((n) => n.status === "unread").length,
    [notifications]
  );

  const counts = useMemo(
    () => ({
      all: notifications.length,
      unread: unreadCount,
      event_reservation: notifications.filter((n) =>
        RESERVATION_TYPES.has(n.type)
      ).length,
      order: notifications.filter((n) => n.type === "order").length,
      support_reply: notifications.filter((n) => n.type === "support_reply")
        .length,
    }),
    [notifications, unreadCount]
  );

  const filtered = useMemo(() => {
    if (activeTab === "all") return notifications;
    if (activeTab === "unread")
      return notifications.filter((n) => n.status === "unread");
    if (activeTab === "event_reservation") {
      return notifications.filter((n) => RESERVATION_TYPES.has(n.type));
    }
    return notifications.filter((n) => n.type === activeTab);
  }, [notifications, activeTab]);

  const formatRelativeTime = (dateValue) => {
    if (!dateValue) return "";
    const date = new Date(dateValue);
    const diffMs = Date.now() - date.getTime();
    if (Number.isNaN(diffMs)) return "";
    const min = Math.round(diffMs / 60000);
    const hr = Math.round(min / 60);
    const day = Math.round(hr / 24);
    if (min < 1) return t("timeJustNow");
    if (min < 60) return t("timeMinutesAgo", { count: min });
    if (hr < 24) return t("timeHoursAgo", { count: hr });
    if (day < 7) return t("timeDaysAgo", { count: day });
    return date.toLocaleString();
  };

  const formatAbsoluteTime = (dateValue) => {
    if (!dateValue) return "";
    const date = new Date(dateValue);
    if (Number.isNaN(date.getTime())) return "";
    return date.toLocaleString(undefined, {
      day: "numeric",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
  };

  const formatGroupLabel = (dateValue) => {
    const date = new Date(dateValue);
    if (Number.isNaN(date.getTime())) return "";
    const startOfDay = (d) =>
      new Date(d.getFullYear(), d.getMonth(), d.getDate());
    const dayDiff = Math.round(
      (startOfDay(new Date()) - startOfDay(date)) / 86400000
    );
    if (dayDiff === 0) return t("today");
    if (dayDiff === 1) return t("yesterday");
    return date.toLocaleDateString(undefined, {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const groups = useMemo(() => {
    return filtered.reduce((acc, notif) => {
      const label = formatGroupLabel(notif.createdAt);
      const last = acc[acc.length - 1];
      if (last && last.label === label) last.items.push(notif);
      else acc.push({ label, items: [notif] });
      return acc;
    }, []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtered]);

  const handleMarkAllAsRead = async () => {
    const unread = notifications.filter((n) => n.status === "unread");
    if (unread.length === 0) return;
    setNotifications((prev) => prev.map((n) => ({ ...n, status: "read" })));
    try {
      await Promise.all(unread.map((n) => functionMarkAsRead(n._id)));
    } catch (error) {
      console.error("Error marking all as read:", error);
    }
  };

  const handleItemClick = async (notif) => {
    if (notif.status === "unread") {
      setNotifications((prev) =>
        prev.map((n) => (n._id === notif._id ? { ...n, status: "read" } : n))
      );
      try {
        await functionMarkAsRead(notif._id);
      } catch (error) {
        console.error("Error marking notification as read:", error);
      }
    }
    const eventId = notif?.extraData?.eventId;
    const supportId = notif?.extraData?.supportId;
    if (notif.type === "event_reservation" && eventId) {
      navigate(`/events/${eventId}/reservations`);
    } else if (notif.type === "table_reservation") {
      navigate("/reservations");
    } else if (notif.type === "client_reclamation") {
      navigate("/client-reclamations");
    } else if (notif.type === "support_reply" && supportId) {
      navigate(`/my-support-requests/${supportId}`);
    } else if (notif.type === "order") {
      navigate("/orders");
    }
  };

  const renderIcon = (type) => {
    if (type === "event_reservation") {
      return (
        <span className="c2e-np__icon c2e-np__icon--event">
          <EventIcon />
        </span>
      );
    }
    if (type === "table_reservation") {
      return (
        <span className="c2e-np__icon c2e-np__icon--table">
          <TableIcon />
        </span>
      );
    }
    if (type === "order") {
      return (
        <span className="c2e-np__icon c2e-np__icon--order">
          <OrderIcon />
        </span>
      );
    }
    if (type === "support_reply") {
      return (
        <span className="c2e-np__icon c2e-np__icon--support">
          <SupportIcon />
        </span>
      );
    }
    return (
      <span className="c2e-np__icon c2e-np__icon--default">
        <BellIcon />
      </span>
    );
  };

  return (
    <div className="c2e-np">
      <header className="c2e-np__hero">
        <div className="c2e-np__hero-copy">
          <span className="c2e-np__heading-icon" aria-hidden="true">
            <BellIcon size={22} />
          </span>
          <div>
            <h1 className="c2e-np__title">{t("notifications")}</h1>
            <p className="c2e-np__subtitle">
              {unreadCount > 0
                ? t("notifUnreadSummary", { count: unreadCount })
                : t("notifAllCaughtUp")}
            </p>
          </div>
        </div>
        <button
          type="button"
          className="c2e-np__markall"
          onClick={handleMarkAllAsRead}
          disabled={unreadCount === 0}
        >
          <CheckIcon />
          {t("markAllAsRead")}
        </button>
      </header>

      <div className="c2e-np__stats" aria-hidden={loading}>
        <div className="c2e-np__stat">
          <strong>{counts.all}</strong>
          <span>{t("notifFilterAll")}</span>
        </div>
        <div className={`c2e-np__stat${unreadCount > 0 ? " c2e-np__stat--hot" : ""}`}>
          <strong>{counts.unread}</strong>
          <span>{t("notifFilterUnread")}</span>
        </div>
        <div className="c2e-np__stat c2e-np__stat--event">
          <strong>{counts.event_reservation}</strong>
          <span>{t("notifFilterReservations")}</span>
        </div>
        <div className="c2e-np__stat c2e-np__stat--order">
          <strong>{counts.order}</strong>
          <span>{t("notifFilterOrders")}</span>
        </div>
        <div className="c2e-np__stat c2e-np__stat--support">
          <strong>{counts.support_reply}</strong>
          <span>{t("notifFilterSupport")}</span>
        </div>
      </div>

      <div className="c2e-np__card">
        <div className="c2e-np__tabs" role="tablist">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={activeTab === tab.id}
              className={`c2e-np__tab${
                activeTab === tab.id ? " c2e-np__tab--active" : ""
              }`}
              onClick={() => setActiveTab(tab.id)}
            >
              {t(tab.labelKey)}
              {counts[tab.id] > 0 && (
                <span className="c2e-np__tab-count">{counts[tab.id]}</span>
              )}
            </button>
          ))}
        </div>

        <div className="c2e-np__body">
          {loading ? (
            <div className="c2e-np__loading">
              <span className="c2e-np__spinner" aria-hidden />
              <span>{t("loading")}</span>
            </div>
          ) : filtered.length === 0 ? (
            <div className="c2e-np__empty">
              <div className="c2e-np__empty-icon" aria-hidden="true">
                <BellIcon size={28} />
              </div>
              <p className="c2e-np__empty-title">{t("noNotifications")}</p>
              <p className="c2e-np__empty-text">{t("notifEmptyHint")}</p>
            </div>
          ) : (
            groups.map((group, groupIdx) => (
              <section
                key={`${group.label}-${groupIdx}`}
                className="c2e-np__group"
              >
                <div className="c2e-np__group-label">
                  <span>{group.label}</span>
                  <span className="c2e-np__group-count">
                    {group.items.length}
                  </span>
                </div>
                <ul className="c2e-np__list">
                  {group.items.map((notif) => (
                    <li key={notif._id}>
                      <button
                        type="button"
                        className={`c2e-np__item${
                          notif.status === "unread"
                            ? " c2e-np__item--unread"
                            : ""
                        }`}
                        onClick={() => handleItemClick(notif)}
                      >
                        {renderIcon(notif.type)}
                        <div className="c2e-np__item-body">
                          <div className="c2e-np__item-top">
                            <p className="c2e-np__item-title">{notif.title}</p>
                            {TYPE_LABEL_KEYS[notif.type] && (
                              <span
                                className={`c2e-np__type c2e-np__type--${notif.type}`}
                              >
                                {t(TYPE_LABEL_KEYS[notif.type])}
                              </span>
                            )}
                          </div>
                          <p className="c2e-np__item-text">{notif.text}</p>
                          {notif.createdAt && (
                            <div className="c2e-np__item-meta">
                              <span className="c2e-np__item-time">
                                {formatRelativeTime(notif.createdAt)}
                              </span>
                              <span className="c2e-np__item-sep" aria-hidden>
                                ·
                              </span>
                              <span className="c2e-np__item-abs">
                                {formatAbsoluteTime(notif.createdAt)}
                              </span>
                            </div>
                          )}
                        </div>
                        <div className="c2e-np__item-side">
                          {notif.status === "unread" && (
                            <span className="c2e-np__item-dot" aria-hidden />
                          )}
                          <span className="c2e-np__item-arrow" aria-hidden>
                            <i className="fas fa-chevron-right" />
                          </span>
                        </div>
                      </button>
                    </li>
                  ))}
                </ul>
              </section>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default Notifications;
